// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef } from "react";
import * as Yup from "yup";
import { useFormik, FormikProvider } from "formik";
import PropTypes from "prop-types";

// mui
import { Stack, Box, IconButton, DialogTitle, DialogContent, DialogActions, Typography, FormControl, Select, Divider } from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";
import { SmartNumberInput } from "../../../components/SmartInputs";

// theme
import {
  B1Tiny,
  ModalActionsLoadingButton,
  ModalContentOutlinedInput,
  ModalContentMenuItem,
  ModalAlertError,
  ModalContentSwitch,
} from "../../../theme/styled";

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const VolumeCalculationDefaults = {
  nVolumeCalculation: "0",
  nRiskProfile: "3",
  dFixLots: "1.00",
  dFixRelRisk: "0.50",
  dFixAbsRisk: "100.00",
  nInosUsedForValueInjection: "0",
  dCommissionPerLot: "0.00",
  bMaxSpread: false,
  nMaxSpread: "20",
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const DEFAULT_LIMITS = {
  dFixLots: {
    digits: 2,
    step: 0.1,
    min: 0.01,
    max: 1000.0,
  },
  dFixRelRisk: {
    digits: 2,
    step: 0.1,
    min: 0.01,
    max: 100.0,
  },
  dFixAbsRisk: {
    digits: 2,
    step: 1.0,
    min: 0.1,
  },
  dCommissionPerLot: {
    digits: 2,
    step: 0.1,
    min: 0.0,
  },
  nMaxSpread: {
    min: 1,
  },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function VolumeCalculationRenderParameters(professionalMode, config, indicators) {
  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Helper function to get display values
  const getDisplayValue = (key) => config[key];

  // Helper function to get indicator name by ID
  const getIndicatorNameById = (indicatorId) => {
    if (!indicatorId || indicatorId === "0") return "None selected";
    if (!indicators || indicators.length === 0) return "No indicators available";

    const indicator = indicators.find((ind) => ind.id.toString() === indicatorId.toString());
    return indicator ? `${indicator.name} (${indicator.subtitle})` : `Unknown indicator (ID: ${indicatorId})`;
  };

  const getRiskProfileDisplay = () => {
    const value = config.nRiskProfile;
    switch (value) {
      case "0":
        return "Lowest";
      case "1":
        return "Low";
      case "2":
        return "Moderate";
      case "3":
        return "Normal";
      case "4":
        return "High";
      case "5":
        return "Extreme";
      default:
        return "Not set";
    }
  };

  const getVolumeCalculationDisplay = () => {
    const value = config.nVolumeCalculation;
    switch (value) {
      case "0":
        return "Fix lots";
      case "1":
        return "Fix relative risk based";
      case "2":
        return "Fix absolute risk based";
      case "3":
        return "Indicator value";
      default:
        return "Not set";
    }
  };

  return (
    <Stack spacing={0.5}>
      {renderParam("Volume calculation method", getVolumeCalculationDisplay())}
      {renderParam("Risk profile", getRiskProfileDisplay())}
      {config.nVolumeCalculation === "0" ? renderParam("Fix lots", getDisplayValue("dFixLots")) : null}
      {config.nVolumeCalculation === "1" ? renderParam("Fix relative risk", getDisplayValue("dFixRelRisk")) : null}
      {config.nVolumeCalculation === "2" ? renderParam("Fix absolute risk", getDisplayValue("dFixAbsRisk")) : null}
      {config.nVolumeCalculation === "3" ? renderParam("Used indicator", getIndicatorNameById(config.nInosUsedForValueInjection)) : null}
      {professionalMode === true ? <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} /> : null}
      {professionalMode === true ? renderParam("Commission per lot", getDisplayValue("dCommissionPerLot")) : null}

      {professionalMode === true ? (
        <>{config.bMaxSpread === true ? renderParam("Maximum spread", getDisplayValue("nMaxSpread")) : renderParam("Maximum spread", "Off")}</>
      ) : null}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const VolumeCalculationConfigure = forwardRef(({ indicators, onSave, initialData = null, professionalMode = null }, ref) => {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  VolumeCalculationConfigure.propTypes = {
    indicators: PropTypes.any,
    onSave: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    professionalMode: PropTypes.any,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [scroll, setScroll] = useState("paper");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const autoCloseTimeoutRef = useRef(null);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    openDialog: () => {
      setOpen(true);
    },
    closeDialog: () => {
      setOpen(false);
    },
  }));

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleReset = () => {
    // Clear all errors and touched state
    setErrors({});
    setTouched({});

    const defaults = {
      ...VolumeCalculationDefaults,
      nVolumeCalculation: values.nVolumeCalculation, // Keep the current selection
    };

    // Apply all the default values to the form
    Object.keys(defaults).forEach((key) => {
      setFieldValue(key, defaults[key]);
    });
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setOpen(false);
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");
    resetForm();
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formik = useFormik({
    initialValues: {
      //  parameters
      nVolumeCalculation: initialData?.nVolumeCalculation ?? VolumeCalculationDefaults.nVolumeCalculation,
      nRiskProfile: initialData?.nRiskProfile ?? VolumeCalculationDefaults.nRiskProfile,
      dFixLots: initialData?.dFixLots ?? VolumeCalculationDefaults.dFixLots,
      dFixRelRisk: initialData?.dFixRelRisk ?? VolumeCalculationDefaults.dFixRelRisk,
      dFixAbsRisk: initialData?.dFixAbsRisk ?? VolumeCalculationDefaults.dFixAbsRisk,
      bMaxSpread: initialData?.bMaxSpread ?? VolumeCalculationDefaults.bMaxSpread,
      nMaxSpread: initialData?.nMaxSpread ?? VolumeCalculationDefaults.nMaxSpread,
      // Updated logic for indicator selection when volume calculation is INOS
      nInosUsedForValueInjection: (() => {
        if (initialData?.nInosUsedForValueInjection) {
          return initialData.nInosUsedForValueInjection;
        }
        // Find first indicator with Signal used for = Forward (2)
        const forwardIndicators = indicators?.filter((ind) => ind.config?.nSignalUsedFor === "2") || [];
        return forwardIndicators.length > 0 ? forwardIndicators[0].id.toString() : VolumeCalculationDefaults.nInosUsedForValueInjection;
      })(),
      dCommissionPerLot: initialData?.dCommissionPerLot ?? VolumeCalculationDefaults.dCommissionPerLot,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting }) => {
      setTimeout(() => {
        try {
          onSave(values);
          setSuccess(true);
          setReturnMessage("Configuration saved successfully!");
          // autoCloseTimeoutRef.current = setTimeout(() => {
          //  handleClose();
          // }, 1500);
        } catch (err) {
          setError(true);
          setReturnMessage("Failed to save configuration.");
          console.error("Save error:", err);
        }
        setSubmitting(false);
      }, 100);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm, setFieldError } = formik;

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  let saveAndCloseDisabled = false;

  if (values.nVolumeCalculation === "3" && indicators && indicators.length === 0) {
    saveAndCloseDisabled = true;
  }
  if (
    values.nVolumeCalculation === "3" &&
    (values.nInosUsedForValueInjection === "" || values.nInosUsedForValueInjection === "0" || values.nInosUsedForValueInjection === 0)
  ) {
    saveAndCloseDisabled = true;
  }

  const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
  if (hasValidationErrors()) {
    saveAndCloseDisabled = true;
  }

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Configure volume calculation
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Configure volume calculation</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                  px: 2,
                  pt: 2,
                  pb: 1,
                  width: "100%",
                  boxSizing: "border-box",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  // Custom scrollbar styling
                  "&::-webkit-scrollbar": {
                    width: "16px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888888",
                    borderRadius: "6px",
                    border: "2px solid #e0e0e0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#666666",
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888888 #e0e0e0",
                }}
              >
                <Stack spacing={2}>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nVolumeCalculation */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter value="Volume calculation method" helpReference="HandleVolumeCalculation" helpText="" helpLink="" fieldName="nVolumeCalculation" touched={touched} errors={errors} />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nVolumeCalculation")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nVolumeCalculation && errors.nVolumeCalculation)}
                        >
                          <ModalContentMenuItem value="0">Fix lots</ModalContentMenuItem>
                          <ModalContentMenuItem value="1">Fix relative risk based</ModalContentMenuItem>
                          <ModalContentMenuItem value="2">Fix absolute risk based</ModalContentMenuItem>
                          {professionalMode === true || values.nVolumeCalculation === "3" ? (
                            <ModalContentMenuItem value="3">Indicator value</ModalContentMenuItem>
                          ) : null}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRiskProfile */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter value="Risk profile" helpReference="HandleVolumeCalculation" helpText="" helpLink="" fieldName="nRiskProfile" touched={touched} errors={errors} />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nRiskProfile")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nRiskProfile && errors.nRiskProfile)}
                        >
                          <ModalContentMenuItem value="0">Lowest</ModalContentMenuItem>
                          <ModalContentMenuItem value="1">Low</ModalContentMenuItem>
                          <ModalContentMenuItem value="2">Moderate</ModalContentMenuItem>
                          <ModalContentMenuItem value="3">Normal</ModalContentMenuItem>
                          <ModalContentMenuItem value="4">High</ModalContentMenuItem>
                          <ModalContentMenuItem value="5">Extreme</ModalContentMenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* dFixLots // TODO */}
                  {/* ---------------------------------------------------------------------- */}
                  {values.nVolumeCalculation === "0" ? (
                    <Box>
                      <SmartParameter value="Fix lots" helpReference="HandleVolumeCalculation" helpText="" helpLink="" fieldName="dFixLots" touched={touched} errors={errors} />
                      <SmartNumberInput textFieldName="dFixLots" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* dFixRelRisk */}
                  {/* ---------------------------------------------------------------------- */}
                  {values.nVolumeCalculation === "1" ? (
                    <Box>
                      <SmartParameter value="Fix relative risk" helpReference="HandleVolumeCalculation" helpText="" helpLink="" fieldName="dFixRelRisk" touched={touched} errors={errors} />
                      <SmartNumberInput textFieldName="dFixRelRisk" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* dFixAbsRisk */}
                  {/* ---------------------------------------------------------------------- */}
                  {values.nVolumeCalculation === "2" ? (
                    <Box>
                      <SmartParameter value="Fix absolute risk" helpReference="HandleVolumeCalculation" helpText="" helpLink="" fieldName="dFixAbsRisk" touched={touched} errors={errors} />
                      <SmartNumberInput textFieldName="dFixAbsRisk" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nInosUsedForValueInjection */}
                  {/* ---------------------------------------------------------------------- */}
                  {values.nVolumeCalculation === "3" &&
                  (() => {
                    const forwardIndicators = indicators?.filter((ind) => ind.config?.nSignalUsedFor === "2") || [];
                    return forwardIndicators.length > 0;
                  })() ? (
                    <Box>
                      <SmartParameter
                        value="Used indicator"
                        helpReference="HandleVolumeCalculation"
                        helpText=""
                        helpLink=""
                        fieldName="nInosUsedForValueInjection"
                        touched={touched}
                        errors={errors}
                      />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nInosUsedForValueInjection")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nInosUsedForValueInjection && errors.nInosUsedForValueInjection)}
                        >
                          <ModalContentMenuItem value="0">
                            <Box>
                              <Typography variant="contentSmall">Please select ...</Typography>
                            </Box>
                          </ModalContentMenuItem>
                          {indicators
                            ?.filter((indicator) => indicator.config?.nSignalUsedFor === "2")
                            .map((indicator) => (
                              <ModalContentMenuItem key={indicator.id} value={indicator.id.toString()}>
                                <Box>
                                  <Typography variant="contentSmall">
                                    {indicator.name} ({indicator.subtitle})
                                  </Typography>
                                </Box>
                              </ModalContentMenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ) : null}

                  {values.nVolumeCalculation === "3" &&
                  (() => {
                    const forwardIndicators = indicators?.filter((ind) => ind.config?.nSignalUsedFor === "2") || [];
                    return forwardIndicators.length === 0;
                  })() ? (
                    <Box sx={{ m: 0, p: 0 }}>
                      <ModalAlertError
                        sx={{
                          m: "0 !important",
                        }}
                        severity="error"
                      >
                        No indicator with <b>Signal used for = Forward</b> is available. Please add at least one indicator and set its{" "}
                        <b>Signal used for</b> setting to <b>Forward</b>.
                      </ModalAlertError>
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  {professionalMode === true ? (
                    <Box>
                      <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* dCommissionPerLot */}
                    {/* ---------------------------------------------------------------------- */}
                    {professionalMode === true ? (
                      <Box>
                        <SmartParameter
                          value="Commission per lot"
                          helpReference="HandleVolumeCalculation"
                          helpText=""
                          helpLink=""
                          fieldName="dCommissionPerLot"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="dCommissionPerLot" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nMaxSpread */}
                    {/* ---------------------------------------------------------------------- */}
                    {professionalMode === true ? (
                      <Box>
                        <SmartParameter value="Maximum spread" helpReference="HandleVolumeCalculation" helpText="" helpLink="" fieldName="nMaxSpread" touched={touched} errors={errors} />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <ModalContentSwitch
                            sx={{ my: "12px" }}
                            checked={values.bMaxSpread === true}
                            onChange={(event) => {
                              setFieldValue("bMaxSpread", event.target.checked);
                            }}
                          />
                          {values.bMaxSpread === true ? (
                            <SmartNumberInput textFieldName="nMaxSpread" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                          ) : null}
                        </Box>
                      </Box>
                    ) : null}
                  </Box>
                </Stack>
              </DialogContent>

              <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
              </Box>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 2,
                  pt: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleReset}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Reset
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    disabled={saveAndCloseDisabled}
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Save
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleClose}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Close
                  </ModalActionsLoadingButton>
                </Box>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </>
  );
});
